import React, { createContext, useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { GridType, PortfolioGameResult } from '../types.ts';
import { FIXED_GAME_CONFIG } from '../constants/fixedGameConfig.ts';
import { FIRESWEEPER_SURVIVOR } from '../constants/gameModes.ts';
import { fireSpreadService } from '../services/fireSpreadService.ts';
import { gameService } from '../services/gameService.ts';
import { tileToIndex } from '../simulation/tileUtils.ts';
import { getDailySeed, getUtcDayKey } from '../simulation/dailySeed.ts';
import { createSeededGameRng } from '../simulation/seededGame.ts';

export type PortfolioPhase = 'selecting' | 'running' | 'finished';

export type SurvivorLeaderboardLocationState = {
  playerResult: PortfolioGameResult;
};

interface PortfolioContextType {
  grid: GridType;
  phase: PortfolioPhase;
  selectedTiles: number[];
  won: boolean | null;
  instantLoss: boolean;
  survivedTileCount: number;
  duration: number;
  dayKey: string;
  seed: number;
  toggleTile: (row: number, col: number) => void;
  setTileSelected: (row: number, col: number, selected: boolean) => void;
  clearSelection: () => void;
  startSimulation: () => void;
  resetGame: () => void;
}

export const PortfolioContext = createContext<PortfolioContextType | null>(null);

const config = FIXED_GAME_CONFIG;

function countSelectedSurvived(grid: GridType, selectedTiles: number[]): number {
  let count = 0;
  for (const index of selectedTiles) {
    const row = Math.floor(index / config.gridSize);
    const col = index % config.gridSize;
    if (grid[row]?.[col] === 'alive') count++;
  }
  return count;
}

function hasSelectedTileBurned(grid: GridType, selectedTiles: number[]): boolean {
  for (const index of selectedTiles) {
    const row = Math.floor(index / config.gridSize);
    const col = index % config.gridSize;
    if (grid[row]?.[col] === 'burned') return true;
  }
  return false;
}

export function PortfolioProvider({ children }: { children: React.ReactNode }) {
  const [grid, setGrid] = useState<GridType>([]);
  const [phase, setPhase] = useState<PortfolioPhase>('selecting');
  const [selectedTiles, setSelectedTiles] = useState<number[]>([]);
  const [won, setWon] = useState<boolean | null>(null);
  const [instantLoss, setInstantLoss] = useState(false);
  const [survivedTileCount, setSurvivedTileCount] = useState(0);
  const [duration, setDuration] = useState(0);
  const [dayKey, setDayKey] = useState(getUtcDayKey());
  const [seed, setSeed] = useState(0);
  const fireIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const startTimeRef = useRef(0);
  const selectedTilesRef = useRef<number[]>([]);
  const dayKeyRef = useRef(getUtcDayKey());
  const seedRef = useRef(0);
  const rngRef = useRef<(() => number) | null>(null);
  const navigate = useNavigate();

  const initializeDailyGrid = useCallback(() => {
    const today = getUtcDayKey();
    const dailySeed = getDailySeed('survivor', today);
    const { rng, firePositions } = createSeededGameRng(
      dailySeed,
      config.gridSize,
      config.startingFires
    );
    dayKeyRef.current = today;
    seedRef.current = dailySeed;
    rngRef.current = rng;
    setDayKey(today);
    setSeed(dailySeed);
    return fireSpreadService.initializeGridFromPositions(
      fireSpreadService.createGrid(config.gridSize),
      firePositions
    );
  }, []);

  const resetGame = useCallback(() => {
    if (fireIntervalRef.current) clearInterval(fireIntervalRef.current);
    fireIntervalRef.current = null;
    setGrid(initializeDailyGrid());
    setPhase('selecting');
    setSelectedTiles([]);
    selectedTilesRef.current = [];
    setWon(null);
    setInstantLoss(false);
    setSurvivedTileCount(0);
    setDuration(0);
  }, [initializeDailyGrid]);

  useEffect(() => {
    setGrid(initializeDailyGrid());
    return () => {
      if (fireIntervalRef.current) clearInterval(fireIntervalRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const clearSelection = useCallback(() => {
    setSelectedTiles([]);
    selectedTilesRef.current = [];
  }, []);

  const finishGame = useCallback(
    (finalGrid: GridType, elapsedSeconds: number, lostInstantly: boolean, didWin: boolean) => {
      const tiles = selectedTilesRef.current;
      const survived = countSelectedSurvived(finalGrid, tiles);

      setGrid(finalGrid);
      setPhase('finished');
      setWon(didWin);
      setInstantLoss(lostInstantly);
      setSurvivedTileCount(survived);
      setDuration(elapsedSeconds);

      const result: PortfolioGameResult = {
        mode: 'portfolio',
        selectedTiles: tiles,
        selectedTileCount: tiles.length,
        survivedTileCount: survived,
        won: didWin,
        instantLoss: lostInstantly,
        duration: elapsedSeconds,
        timestamp: Date.now(),
        config,
        finalGrid,
        dayKey: dayKeyRef.current,
        seed: seedRef.current,
      };

      void gameService
        .savePortfolioResult(result)
        .then((id) => {
          navigate(FIRESWEEPER_SURVIVOR.leaderboardPath, {
            replace: true,
            state: { playerResult: { ...result, id } } satisfies SurvivorLeaderboardLocationState,
          });
        })
        .catch((error) => {
          console.error(error);
          navigate(FIRESWEEPER_SURVIVOR.leaderboardPath, {
            replace: true,
            state: { playerResult: result } satisfies SurvivorLeaderboardLocationState,
          });
        });
    },
    [navigate]
  );

  const startSimulation = useCallback(() => {
    if (selectedTilesRef.current.length === 0 || phase !== 'selecting') return;

    if (fireIntervalRef.current) clearInterval(fireIntervalRef.current);

    setPhase('running');
    startTimeRef.current = Date.now();

    const interval = setInterval(() => {
      setGrid((currentGrid) => {
        const rng = rngRef.current ?? Math.random;
        const updatedGrid = fireSpreadService.updateGrid(
          currentGrid,
          {
            ...config,
            isTurnBased: false,
          },
          rng
        );
        const elapsed = Math.round((Date.now() - startTimeRef.current) / 1000);

        if (hasSelectedTileBurned(updatedGrid, selectedTilesRef.current)) {
          clearInterval(interval);
          fireIntervalRef.current = null;
          finishGame(updatedGrid, elapsed, true, false);
          return updatedGrid;
        }

        const status = fireSpreadService.checkGameOver(updatedGrid);
        if (status) {
          clearInterval(interval);
          fireIntervalRef.current = null;
          const survived = countSelectedSurvived(updatedGrid, selectedTilesRef.current);
          const allSurvived =
            selectedTilesRef.current.length > 0 && survived === selectedTilesRef.current.length;
          finishGame(updatedGrid, elapsed, false, allSurvived);
        }

        return updatedGrid;
      });
    }, config.fireInterval);

    fireIntervalRef.current = interval;
  }, [phase, finishGame]);

  const toggleTile = useCallback(
    (row: number, col: number) => {
      if (phase !== 'selecting') return;
      if (grid[row]?.[col] !== 'alive') return;

      const index = tileToIndex(row, col, config.gridSize);
      setSelectedTiles((prev) => {
        const next = prev.includes(index)
          ? prev.filter((t) => t !== index)
          : [...prev, index];
        selectedTilesRef.current = next;
        return next;
      });
    },
    [phase, grid]
  );

  const setTileSelected = useCallback(
    (row: number, col: number, selected: boolean) => {
      if (phase !== 'selecting') return;
      if (selected && grid[row]?.[col] !== 'alive') return;

      const index = tileToIndex(row, col, config.gridSize);
      setSelectedTiles((prev) => {
        const has = prev.includes(index);
        if (selected === has) return prev;

        const next = selected ? [...prev, index] : prev.filter((t) => t !== index);
        selectedTilesRef.current = next;
        return next;
      });
    },
    [phase, grid]
  );

  return (
    <PortfolioContext.Provider
      value={{
        grid,
        phase,
        selectedTiles,
        won,
        instantLoss,
        survivedTileCount,
        duration,
        dayKey,
        seed,
        toggleTile,
        setTileSelected,
        clearSelection,
        startSimulation,
        resetGame,
      }}
    >
      {children}
    </PortfolioContext.Provider>
  );
}
